import { useState, useEffect, useCallback } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase/config';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';


import styled from 'styled-components';

const AdminContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;
  
  @media (max-width: 768px) {
    align-items: center;
    padding: 16px;
  }
`;

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e9ecef;
  width: 100%;

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  h2 {
    margin: 0;
    color: #212529;
    font-size: 24px;
  }
  
  .subtitle {
    color: #6c757d;
    font-size: 14px;
    margin-top: 4px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    
    > div:first-child {
      justify-content: center;
    }
  }
`;



const AdminCard = styled.div`
  width: 100%;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const LoadingCard = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
`;

const RefreshButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #218838;
  }
`;

const TableInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const TableScrollContainer = styled.div`
  overflow: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
  width: 100%;
`;

const DataTableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #e3f2fd !important;
    transform: scale(1.001);
  }
  
  &:nth-child(even) {
    background: #f9f9f9;
  }
  
  &:nth-child(even):hover {
    background: #e3f2fd !important;
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
  
  thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  th {
    background: #495057;
    color: white;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    border-bottom: 2px solid #dee2e6;
    white-space: nowrap;
    min-width: 120px;
    position: sticky;
    top: 0;
  }
  
  td {
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    font-size: 13px;
    white-space: nowrap;
    min-width: 120px;
  }
`;

const ClickableRow = styled(DataTableRow)`
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #e3f2fd !important;
    transform: scale(1.001);
  }
  
  &:nth-child(even) {
    background: #f9f9f9;
  }
  
  &:nth-child(even):hover {
    background: #e3f2fd !important;
  }
`;

const ViewResponseButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #218838;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
  width: 90%;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 32px;
  border-bottom: 2px solid #e9ecef;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  margin: -32px -32px 24px -32px;

  h2 {
    margin: 0;
    color: #212529;
    font-size: 24px;
  }
  
  .subtitle {
    color: #6c757d;
    font-size: 14px;
    margin-top: 4px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #dee2e6;
`;

const BaseButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(BaseButton)`
  background: #6c757d;
  
  &:hover:not(:disabled) {
    background: #5a6268;
  }
`;

const SaveButton = styled(BaseButton)`
  background: #0d6efd;
  
  &:hover:not(:disabled) {
    background: #0b5ed7;
  }
`;

const DetailCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border-left: 4px solid #0d6efd;

  h3 {
    color: #212529;
    margin: 0 0 16px 0;
    font-size: 18px;
    font-weight: 600;
  }
  
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
  }
  
  .detail-item {
    margin-bottom: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f8f9fa;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .detail-grid .detail-item {
    background: white;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #dee2e6;
    margin-bottom: 0;
  }
  
  .label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 4px;
  }
  
  .value {
    color: #212529;
  }
  
  .boolean-value {
    color: #0d6efd;
    font-weight: 500;
  }
  
  .array-value {
    color: #6f42c1;
  }
  
  .empty-value {
    color: #6c757d;
    font-style: italic;
  }
`;





const UserInfoButton = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #dc3545;
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.15);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const UserName = styled.span`
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const UserEmail = styled.span`
  font-size: 12px;
  color: #6c757d;
`;

const LogoutIcon = styled.div`
  background: #dc3545;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 0.3s ease;
  
  ${UserInfoButton}:hover & {
    background: #c82333;
  }
`;

const StatusBadge = styled.span`
  background: #28a745;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaginationButton = styled.button`
  background: #0d6efd;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #0b5ed7;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const PageSizeSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #0d6efd;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  &.primary {
    border-left-color: #0d6efd;
  }
  
  &.success {
    border-left-color: #28a745;
  }
  
  &.warning {
    border-left-color: #ffc107;
  }
  
  &.danger {
    border-left-color: #dc3545;
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
`;

const StatDescription = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  line-height: 1.4;
`;

const FiltersContainer = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #dee2e6;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
  }
`;

const Button = styled.button`
  background: #0d6efd;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #0b5ed7;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const ActiveFiltersContainer = styled.div`
  margin-top: 16px;
  
  h4 {
    margin: 0 0 12px 0;
    color: #495057;
    font-size: 14px;
  }
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const FilterTag = styled.div`
  background: #e3f2fd;
  color: #1976d2;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #bbdefb;
  }
`;

const RemoveFilterButton = styled.button`
  background: none;
  border: none;
  color: #d32f2f;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: #ffebee;
  }
`;

const ClearAllFiltersButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #c82333;
  }
`;

const DataTableContainer = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

interface DashboardStats {
  totalOSCs: number;
  oscsWithResponses: number;
  oscsWithoutCompleteResponses: number;
  oscsWithPartialResponses: number;
  totalResponses: number;
}

const AdminDashboard: React.FC = () => {
  const { currentUser, logout, changePassword } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalOSCs: 0,
    oscsWithResponses: 0,
    oscsWithoutCompleteResponses: 0,
    oscsWithPartialResponses: 0,
    totalResponses: 0
  });
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState<string>('');
  
  // Estados para o sistema de dados e filtros
  const [allResponses, setAllResponses] = useState<any[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedFilterField, setSelectedFilterField] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('=');
  const [filterValue, setFilterValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{field: string, operator: string, value: string}[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginatedResponses, setPaginatedResponses] = useState<any[]>([]);
  
  // Estados para o modal de OSCs sem respostas
  const [showOSCsWithoutResponsesModal, setShowOSCsWithoutResponsesModal] = useState(false);
  const [oscsWithoutResponses, setOscsWithoutResponses] = useState<any[]>([]);
  const [loadingOSCsWithoutResponses, setLoadingOSCsWithoutResponses] = useState(false);
  const [oscsWithoutResponsesSearchTerm, setOscsWithoutResponsesSearchTerm] = useState('');
  
  // Estados para o modal de Respostas em Andamento
  const [showPartialResponsesModal, setShowPartialResponsesModal] = useState(false);
  const [oscsWithPartialResponses, setOscsWithPartialResponses] = useState<any[]>([]);
  const [loadingOSCsWithPartialResponses, setLoadingOSCsWithPartialResponses] = useState(false);
  const [partialResponsesSearchTerm, setPartialResponsesSearchTerm] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const fetchAdminName = useCallback(async () => {
    if (currentUser?.uid) {
      try {
        const adminRef = ref(database, `admin/${currentUser.uid}/nome`);
        const adminSnapshot = await get(adminRef);
        if (adminSnapshot.exists()) {
          setAdminName(adminSnapshot.val());
        }
      } catch (error) {
        
      }
    }
  }, [currentUser?.uid]);

  // Campos disponíveis para filtro
  const availableFields = [
    { key: 'nomeOrganizacao', label: 'Nome da Organização', type: 'text' },
    { key: 'cnpj', label: 'CNPJ', type: 'text' },
    { key: 'email', label: 'E-mail', type: 'text' },
    { key: 'telefone', label: 'Telefone', type: 'text' },
    { key: 'nomeResponsavel', label: 'Nome do Responsável', type: 'text' },
    { key: 'cargoResponsavel', label: 'Cargo do Responsável', type: 'text' },
    { key: 'emailResponsavel', label: 'Email do Responsável', type: 'text' },
    { key: 'numeroFuncionarios', label: 'Número de Funcionários', type: 'number' },
    { key: 'numeroVoluntarios', label: 'Número de Voluntários', type: 'number' },
    { key: 'pessoasAtendidasMes', label: 'Pessoas Atendidas por Mês', type: 'number' },
    { key: 'publicoAtendido', label: 'Públicos Atendidos', type: 'select' },
    { key: 'endereco', label: 'Endereço', type: 'text' },
    { key: 'uf', label: 'UF', type: 'text' },
    { key: 'cidade', label: 'Cidade', type: 'text' },
    { key: 'responseDate', label: 'Data da Resposta', type: 'date' },
    { key: 'suspendeAtividades', label: 'Suspende Atividades', type: 'boolean' },
    { key: 'mesesSuspensao', label: 'Mêses Suspensão', type: 'select' },
    { key: 'documentos', label: 'Documentos', type: 'select' },
    { key: 'criancas', label: 'Número de Crianças', type: 'number' },
    { key: 'adolescentes', label: 'Número de Adolescentes', type: 'number' },
    { key: 'jovens', label: 'Número de Jovens', type: 'number' },
    { key: 'adultos', label: 'Número de Adultos', type: 'number' },
    { key: 'idosos', label: 'Número de Idosos', type: 'number' },
    { key: 'possuiFichaCadastral', label: 'Possui Ficha Cadastral', type: 'boolean' },
    { key: 'infraestrutura', label: 'Infraestrutura', type: 'select' }
  ];

  // Opções disponíveis para o campo publicoAtendido
  const publicoAtendidoOptions = [
    'Afrodescendentes', 'Crianças', 'Famílias em vulnerabilidade', 'Idosos',
    'Imigrantes', 'Indígenas', 'Mães solo/solteiras', 'Mulheres',
    'Pessoas com câncer', 'Pessoas com deficiência', 'Pessoas com dependência química',
    'Pessoas com HIV', 'Pessoas em situação de rua', 'Pessoas LGBTQIAPNB+',
    'Pessoas vítimas de violência', 'Quilombolas', 'Refugiados', 'Outros'
  ];

  // Opções para o campo mesesSuspensao
  const mesesSuspensaoOptions = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Opções para o campo documentos
  const documentosOptions = [
    'Convênio com prefeituras ou outros órgãos públicos',
    'Convênio específico para alimentação',
    'Cartão CNPJ atualizado',
    'Estatuto Social com RG e CPF dos membros atualizado',
    'Ata da Posse de Assembleia com RG e CPF dos membros atualizado',
    'Alvará Sanitário vigente'
  ];

  // Opções para o campo infraestrutura
  const infraestruturaOptions = [
    'Cozinha',
    'Espaço adequado para armazenamento de alimentos',
    'Profissionais com formação em Nutrição e/ou Gastronomia',
    'Refeitório',
    'Sistema de refrigeração (geladeira e/ou freezer)'
  ];

  // Opções para campos boolean
  const booleanOptions = ['Sim', 'Não'];

  // Operadores disponíveis
  const getAvailableOperators = (fieldType: string) => {
    if (fieldType === 'number' || fieldType === 'date') {
      return [
        { value: '=', label: 'Igual a (=)' },
        { value: '<>', label: 'Diferente de (<>)' },
        { value: '>', label: 'Maior que (>)' },
        { value: '>=', label: 'Maior ou igual (>=)' },
        { value: '<', label: 'Menor que (<)' },
        { value: '<=', label: 'Menor ou igual (<=)' }
      ];
    }

    if (fieldType === 'select') {
      return [
        { value: '=', label: 'Contém' },
        { value: '<>', label: 'Não contém' }
      ];
    }

    if (fieldType === 'boolean') {
      return [
        { value: '=', label: 'Igual a (=)' },
        { value: '<>', label: 'Diferente de (<>)' }
      ];
    }

    // Para campos de texto
    return [
      { value: 'contains', label: 'Contém' },
      { value: 'not_contains', label: 'Não contém' },
      { value: '=', label: 'Igual a (=)' },
      { value: '<>', label: 'Diferente de (<>)' }
    ];
  };

  // Função para obter label do campo
  const getFieldLabel = (key: string) => {
    const field = availableFields.find(f => f.key === key);
    return field ? field.label : key;
  };

  // Carregar dados das respostas
  const loadResponsesData = async () => {
    try {
      setLoadingData(true);
      
      // Carregar respostas
      const responsesRef = ref(database, 'responses');
      const responsesSnapshot = await get(responsesRef);
      
      // Carregar OSCs
      const oscsRef = ref(database, 'oscs');
      const oscsSnapshot = await get(oscsRef);
      
      if (responsesSnapshot.exists()) {
        const responsesData = responsesSnapshot.val();
        const oscsData = oscsSnapshot.exists() ? oscsSnapshot.val() : {};
        
        const responsesArray = Object.entries(responsesData).map(([id, data]: [string, any]) => {
          // Buscar dados da OSC correspondente
          const oscData = data.oscId ? oscsData[data.oscId] : null;
          
          return {
            id,
            ...data,
            // Sobrescrever com dados da OSC se existirem
            nomeOrganizacao: oscData?.razaoSocial || data.nomeOrganizacao || 'N/A',
            cnpj: oscData?.cnpj || data.cnpj || 'N/A',
            email: oscData?.email || data.email || 'N/A',
            telefone: oscData?.whatsappResponsavel || data.telefone || 'N/A',
            nomeResponsavel: oscData?.nomeResponsavel || data.nomeResponsavel || 'N/A',
            cargoResponsavel: oscData?.cargoResponsavel || data.cargoResponsavel || 'N/A',
            emailResponsavel: oscData?.emailResponsavel || data.emailResponsavel || 'N/A',
            numeroFuncionarios: oscData?.numeroFuncionarios || data.numeroFuncionarios || 'N/A',
            numeroVoluntarios: oscData?.numeroVoluntarios || data.numeroVoluntarios || 'N/A',
            pessoasAtendidasMes: oscData?.pessoasAtendidasMes || data.pessoasAtendidasMes || 'N/A',
            endereco: oscData?.endereco || data.endereco || 'N/A',
            uf: oscData?.uf || data.uf || 'N/A',
            cidade: oscData?.cidade || data.cidade || 'N/A'
          };
        });
        
        // Ordenar por data de criação (mais recente primeiro)
        responsesArray.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        
        // Filtrar para manter apenas a resposta mais recente de cada OSC
        const latestResponsesByOSC = new Map();
        responsesArray.forEach(response => {
          const oscId = response.oscId;
          if (!latestResponsesByOSC.has(oscId) || 
              new Date(response.createdAt).getTime() > new Date(latestResponsesByOSC.get(oscId).createdAt).getTime()) {
            latestResponsesByOSC.set(oscId, response);
          }
        });
        
        const filteredLatestResponses = Array.from(latestResponsesByOSC.values())
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        
        setAllResponses(filteredLatestResponses);
        setFilteredResponses(filteredLatestResponses);
      } else {
        setAllResponses([]);
        setFilteredResponses([]);
      }
    } catch (error) {
      
    } finally {
      setLoadingData(false);
    }
  };

  // Aplicar filtros
  const applyFilters = () => {
    

    let filtered = [...allResponses];
    
    activeFilters.forEach((filter, filterIndex) => {
      
      const beforeFilterCount = filtered.length;
      
      filtered = filtered.filter(response => {
        // Mapear responseDate para createdAt
        const actualField = filter.field === 'responseDate' ? 'createdAt' : filter.field;
        const fieldValue = response[actualField];
        
        if (fieldValue === null || fieldValue === undefined) {
          
          return false;
        }
        
        const field = availableFields.find(f => f.key === filter.field);
        const fieldType = field?.type || 'text';
        
        const result = compareValues(fieldValue, filter.operator, filter.value, fieldType);
        
        if (!result) {
          
        }
        
        return result;
      });
      
      const afterFilterCount = filtered.length;
      
    });
    
    
    
    setFilteredResponses(filtered);
  };

  // Função para comparar valores com base no operador
  const compareValues = (fieldValue: any, operator: string, filterValue: string, fieldType: string): boolean => {
    

    if (fieldType === 'number') {
      const numFieldValue = parseFloat(String(fieldValue));
      const numFilterValue = parseFloat(filterValue);
      
      
      
      if (isNaN(numFieldValue) || isNaN(numFilterValue)) {
        
        return false;
      }
      
      let result = false;
      switch (operator) {
        case '=':
          result = numFieldValue === numFilterValue;
          break;
        case '<>':
          result = numFieldValue !== numFilterValue;
          break;
        case '>':
          result = numFieldValue > numFilterValue;
          break;
        case '>=':
          result = numFieldValue >= numFilterValue;
          break;
        case '<':
          result = numFieldValue < numFilterValue;
          break;
        case '<=':
          result = numFieldValue <= numFilterValue;
          break;
        default:
          result = false;
      }
      
      return result;
    } else if (fieldType === 'date') {
      // Extrair apenas a parte da data (YYYY-MM-DD) ignorando horário e fuso horário
      const extractDateString = (dateValue: string): string => {
        if (dateValue.includes('T')) {
          // Se é ISO string (2025-08-14T11:42:34.145Z), pegar apenas a parte da data
          return dateValue.split('T')[0];
        } else if (dateValue.includes(' ')) {
          // Se é formato com espaço (2025-09-08 19:12:49.220904+00), pegar apenas a parte da data
          return dateValue.split(' ')[0];
        }
        // Se já é apenas data (2025-08-14), retornar como está
        return dateValue;
      };
      
      const fieldDateString = extractDateString(String(fieldValue));
      const filterDateString = extractDateString(String(filterValue));
      
      
      
      // Validar formato das datas (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(fieldDateString) || !dateRegex.test(filterDateString)) {
        
        return false;
      }
      
      // Converter para números para comparação (YYYYMMDD)
      const fieldDateNumber = parseInt(fieldDateString.replace(/-/g, ''));
      const filterDateNumber = parseInt(filterDateString.replace(/-/g, ''));
      
      
      
      let result = false;
      switch (operator) {
        case '=':
          result = fieldDateNumber === filterDateNumber;
          break;
        case '<>':
          result = fieldDateNumber !== filterDateNumber;
          break;
        case '>':
          result = fieldDateNumber > filterDateNumber;
          break;
        case '>=':
          result = fieldDateNumber >= filterDateNumber;
          break;
        case '<':
          result = fieldDateNumber < filterDateNumber;
          break;
        case '<=':
          result = fieldDateNumber <= filterDateNumber;
          break;
        default:
          result = false;
      }
      
      return result;
    } else if (fieldType === 'select') {
      // Campos de seleção múltipla (arrays)
      if (Array.isArray(fieldValue)) {
        switch (operator) {
          case '=':
            return fieldValue.includes(filterValue);
          case '<>':
            return !fieldValue.includes(filterValue);
          default:
            return false;
        }
      } else {
        // Se não for array, tratar como string
        const strFieldValue = String(fieldValue).toLowerCase();
        const strFilterValue = filterValue.toLowerCase();
        
        switch (operator) {
          case '=':
            return strFieldValue.includes(strFilterValue);
          case '<>':
            return !strFieldValue.includes(strFilterValue);
          default:
            return false;
        }
      }
    } else if (fieldType === 'boolean') {
      // Campos booleanos
      
      
      // Converter o valor do filtro ("Sim"/"Não") para boolean
      const booleanFilterValue = filterValue === 'Sim';
      
      
      
      let result = false;
      switch (operator) {
        case '=':
          result = fieldValue === booleanFilterValue;
          break;
        case '<>':
          result = fieldValue !== booleanFilterValue;
          break;
        default:
          result = false;
      }
      
      
      return result;
    } else {
      // Campos de texto
      const strFieldValue = String(fieldValue).toLowerCase();
      const strFilterValue = filterValue.toLowerCase();
      
      switch (operator) {
        case 'contains':
          return strFieldValue.includes(strFilterValue);
        case 'not_contains':
          return !strFieldValue.includes(strFilterValue);
        case '=':
          return strFieldValue === strFilterValue;
        case '<>':
          return strFieldValue !== strFilterValue;
        default:
          return false;
      }
    }
  };

  // Adicionar filtro
  const addFilter = () => {
    if (selectedFilterField && filterValue) {
      const newFilter = { field: selectedFilterField, operator: selectedOperator, value: filterValue };
      const updatedFilters = [...activeFilters, newFilter];
      setActiveFilters(updatedFilters);
      setSelectedFilterField('');
      setSelectedOperator('=');
      setFilterValue('');
    }
  };

  // Função para obter o tipo do campo selecionado
  const getSelectedFieldType = () => {
    const field = availableFields.find(f => f.key === selectedFilterField);
    return field?.type || 'text';
  };

  // Função para obter o label do operador
  const getOperatorLabel = (operator: string) => {
    const allOperators = getAvailableOperators('number'); // Pega todos os operadores possíveis
    const operatorObj = allOperators.find(op => op.value === operator);
    return operatorObj ? operatorObj.label : operator;
  };

  // Remover filtro
  const removeFilter = (index: number) => {
    const updatedFilters = activeFilters.filter((_, i) => i !== index);
    setActiveFilters(updatedFilters);
  };

  // Limpar todos os filtros
  const clearAllFilters = () => {
    setActiveFilters([]);
    setFilteredResponses(allResponses);
  };

  // Editar filtro ativo
  const editFilter = (index: number) => {
    const filter = activeFilters[index];
    setSelectedFilterField(filter.field);
    setSelectedOperator(filter.operator);
    setFilterValue(filter.value);
    // Remove o filtro da lista para permitir edição
    removeFilter(index);
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    // Exibir data sem conversão de fuso horário
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função para aplicar máscara de CNPJ
  const applyCnpjMask = (value: string) => {
    // Remove tudo que não é dígito
    const cleanValue = value.replace(/\D/g, '');
    
    // Aplica a máscara XX.XXX.XXX/XXXX-XX
    if (cleanValue.length <= 14) {
      return cleanValue
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return cleanValue.slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  // Manipular mudança no valor do filtro
  const handleFilterValueChange = (value: string) => {
    if (selectedFilterField === 'cnpj') {
      setFilterValue(applyCnpjMask(value));
    } else {
      setFilterValue(value);
    }
  };

  // Ver detalhes da resposta
  const viewResponseDetails = (response: any) => {
    setSelectedResponse(response);
  };

  // Funções de controle da paginação
  const totalPages = Math.ceil(filteredResponses.length / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, filteredResponses.length);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Função para filtrar campos dependentes baseado nas condições
  const filterDependentFields = (data: any) => {
    const filteredData = { ...data };
    
    // Regras de dependência
    const dependencyRules = [
      // Distribuição de alimentos
      {
        parentField: 'distribuicaoAlimentos',
        parentValue: 'Não',
        dependentFields: [
          'tipoAlimentoDistribuido', 'frequenciaDistribuicao', 'numeroFamiliasAtendidas',
          'parceirosFornecedores', 'origemAlimentos', 'criteriosSelecao',
          'acompanhamentoNutricional', 'educacaoNutricional'
        ]
      },
      // Fornecimento de refeições
      {
        parentField: 'fornecimentoRefeicoes',
        parentValue: 'Não',
        dependentFields: [
          'tipoRefeicao', 'numeroRefeicoesServidas', 'numeroRefeicoesServidas',
          'localRefeicoes', 'horarioRefeicoes', 'acompanhamentoNutricionalRefeicoes'
        ]
      },
      // Outras atividades
      {
        parentField: 'outrasAtividades',
        parentValue: 'Não',
        dependentFields: ['descricaoOutrasAtividades']
      }
    ];
    
    // Aplicar regras de filtragem
    dependencyRules.forEach(rule => {
      if (filteredData[rule.parentField] === rule.parentValue) {
        rule.dependentFields.forEach(field => {
          delete filteredData[field];
        });
      }
    });
    
    return filteredData;
  };

  const formatValue = (value: any) => {
    if (typeof value === 'boolean') {
      return <span className="boolean-value">{value ? 'Sim' : 'Não'}</span>;
    }
    if (Array.isArray(value)) {
      return <span className="array-value">{value.join(', ')}</span>;
    }
    if (typeof value === 'number') {
      return <span className="value">{value.toLocaleString('pt-BR')}</span>;
    }
    return <span className="value">{value || <span className="empty-value">Não informado</span>}</span>;
  };

  const getDetailFieldLabel = (key: string) => {
    const labels: { [key: string]: string } = {
      nomeOrganizacao: 'Nome da Organização',
      cnpj: 'CNPJ',
      endereco: 'Endereço',
      telefone: 'Telefone',
      email: 'E-mail',
      nomeResponsavel: 'Nome do Responsável',
      cargoResponsavel: 'Cargo do Responsável',
      emailResponsavel: 'Email do Responsável',
      atividades: 'Atividades da OSC',
      numeroFuncionarios: 'Número de funcionários',
      numeroVoluntarios: 'Número de voluntários',
      pessoasAtendidasMes: 'Pessoas atendidas por mês',
      suspendeAtividades: 'Suspende atividades em algum período',
      mesesSuspensao: 'Meses de suspensão das atividades',
      documentos: 'Documentos que a entidade possui',
      publicoAtendido: 'Público atendido',
      publicoAtendidoOutros: 'Outros públicos atendidos',
      criancas: 'Crianças (0 a 12 anos)',
      adolescentes: 'Adolescentes (13 a 17 anos)',
      jovens: 'Jovens (18 a 29 anos)',
      adultos: 'Adultos (30 a 59 anos)',
      idosos: 'Pessoas Idosas (acima dos 60 anos)',
      possuiFichaCadastral: 'Possui ficha cadastral com dados socioeconômicos dos atendidos',
      beneficiariosMensais: 'Beneficiários mensais (alimentos)',
      infraestrutura: 'Infraestrutura que a entidade possui',
      serveRefeicoes: 'Servem refeições no local',
      serveCafeManha: 'Servem Café da manhã',
      pessoasCafeManha: 'Pessoas servidas - Café da manhã (mês)',
      diasCafeManha: 'Dias por semana - Café da manhã',
      serveLancheManha: 'Servem Lanche da manhã',
      pessoasLancheManha: 'Pessoas servidas - Lanche da manhã (mês)',
      diasLancheManha: 'Dias por semana - Lanche da manhã',
      serveAlmoco: 'Servem Almoço',
      pessoasAlmoco: 'Pessoas servidas - Almoço (mês)',
      diasAlmoco: 'Dias por semana - Almoço',
      serveLancheTarde: 'Servem Lanche da Tarde',
      pessoasLancheTarde: 'Pessoas servidas - Lanche da tarde (mês)',
      diasLancheTarde: 'Dias por semana - Lanche da tarde',
      serveJantar: 'Servem Jantar',
      pessoasJantar: 'Pessoas servidas - Jantar (mês)',
      diasJantar: 'Dias por semana - Jantar',
      distribuiAlimentos: 'Distribui Alimentos',
      distribuiCestasBasicas: 'Distribui Cestas Básicas',
      quantidadeCestasBasicas: 'Quantidade de Cestas Básicas por Mês',
      distribuiMarmitas: 'Distribui Marmitas/Quentinhas',
      quantidadeMarmitas: 'Quantidade de Marmitas/Quentinhas por Mês',
      distribuiKitFrutas: 'Distribui Kit/Sacolinha de Frutas, Legumes, Verduras',
      quantidadeKitFrutas: 'Quantidade de Kit/Sacolinha por Mês',
      atividadesAlimentacao: 'Atividades Voltadas para Alimentação',
      tiposAtividadesAlimentacao: 'Tipos de Atividades de Alimentação',
      outrasAtividadesAlimentacao: 'Outras Atividades de Alimentação',
      descricaoAtividadesAlimentacao: 'Descrição das Atividades de Alimentação',
      transfereDoacoes: 'Transfere Doações para Outras Unidades',
      unidadesBeneficiadas: 'Unidades/Filiais Beneficiadas',
      restricoesColeta: 'Possui Restrições para Coletar Doações',
      descricaoRestricoes: 'Descrição das Restrições',
      disponibilidadeColeta: 'Disponibilidade para Coletar Doações',
      recebeDoacoesOutrosParceiros: 'Recebe Doações de Outros Parceiros',
      tiposAlimentosRecebidos: 'Tipos de Alimentos Recebidos',
      outrosAlimentosRecebidos: 'Outros Alimentos Recebidos',
      recebeBancoAlimentos: 'Recebe Doação de Banco de Alimentos',
      quaisBancosAlimentos: 'Quais Bancos de Alimentos',
      recebeSescMesaBrasil: 'Recebe Doação do Sesc Mesa Brasil',
      quaisUnidadesSesc: 'Quais Unidades do Sesc Mesa Brasil',
      recebeGoverno: 'Recebe Doação do Governo',
      recebePessoasFisicas: 'Recebe Doação de Pessoas Físicas',
      frequenciaDoacoesOutrosParceiros: 'Frequência de Doações de Outros Parceiros',
      possuiTransporte: 'Possui Transporte',
      tipoTransporte: 'Tipo de Transporte',
      veiculoColeta: 'Veículo para Coleta',
      avaliacaoFormulario: 'Avaliação do Formulário',
      comentarioExperiencia: 'Comentário sobre a Experiência',
      probabilidadeRecomendacao: 'Probabilidade de Recomendação',
      uf: 'UF',
      cidade: 'Cidade'
    };
    return labels[key] || key;
  };

  // Ordem das perguntas conforme aparecem no formulário
  const questionOrder = [
    'nomeOrganizacao',
    'cnpj',
    'endereco',
    'telefone',
    'email',
    'nomeResponsavel',
    'cargoResponsavel',
    'emailResponsavel',
    'atividades',
    'numeroFuncionarios',
    'numeroVoluntarios',
    'pessoasAtendidasMes',
    'suspendeAtividades',
    'mesesSuspensao',
    'documentos',
    'publicoAtendido',
    'publicoAtendidoOutros',
    'criancas',
    'adolescentes',
    'jovens',
    'adultos',
    'idosos',
    'possuiFichaCadastral',
    'beneficiariosMensais',
    'infraestrutura',
    'serveRefeicoes',
    'serveCafeManha',
    'pessoasCafeManha',
    'diasCafeManha',
    'serveLancheManha',
    'pessoasLancheManha',
    'diasLancheManha',
    'serveAlmoco',
    'pessoasAlmoco',
    'diasAlmoco',
    'serveLancheTarde',
    'pessoasLancheTarde',
    'diasLancheTarde',
    'serveJantar',
    'pessoasJantar',
    'diasJantar',
    'distribuiAlimentos',
    'distribuiCestasBasicas',
    'quantidadeCestasBasicas',
    'distribuiMarmitas',
    'quantidadeMarmitas',
    'distribuiKitFrutas',
    'quantidadeKitFrutas',
    'atividadesAlimentacao',
    'tiposAtividadesAlimentacao',
    'outrasAtividadesAlimentacao',
    'descricaoAtividadesAlimentacao',
    'transfereDoacoes',
    'unidadesBeneficiadas',
    'restricoesColeta',
    'descricaoRestricoes',
    'disponibilidadeColeta',
    'recebeDoacoesOutrosParceiros',
    'tiposAlimentosRecebidos',
    'outrosAlimentosRecebidos',
    'recebeBancoAlimentos',
    'quaisBancosAlimentos',
    'recebeSescMesaBrasil',
    'quaisUnidadesSesc',
    'recebeGoverno',
    'recebePessoasFisicas',
    'frequenciaDoacoesOutrosParceiros',
    'possuiTransporte',
    'tipoTransporte',
    'veiculoColeta',
    'avaliacaoFormulario',
    'comentarioExperiencia',
    'probabilidadeRecomendacao'
  ];

  // Exportar para CSV
  const exportToCSV = () => {
    if (filteredResponses.length === 0) {
      alert('Não há dados para exportar.');
      return;
    }

    // Preparar dados para exportação com todos os campos em ordem
    const exportData = filteredResponses.map(response => {
      const row: any = {};
      
      // Campos de identificação da OSC
      row['ID da Resposta'] = "'" + (response.id || 'N/A');
      row['ID da OSC'] = response.oscId || 'N/A';
      row['Data de Criação'] = formatDate(response.createdAt);
      
      // Adicionar todas as perguntas na ordem definida
      questionOrder.forEach(key => {
        const label = getDetailFieldLabel(key);
        let value = response[key];
        
        // Formatar valores especiais
        if (Array.isArray(value)) {
          value = value.join(', ');
        } else if (typeof value === 'boolean') {
          value = value ? 'Sim' : 'Não';
        } else if (value === null || value === undefined || value === '') {
          value = 'N/A';
        }
        
        row[label] = value;
      });
      
      // Adicionar campos que não estão na ordem definida (para compatibilidade)
      Object.keys(response).forEach(key => {
        if (!questionOrder.includes(key) && !['id', 'oscId', 'createdAt'].includes(key)) {
          const label = getDetailFieldLabel(key);
          let value = response[key];
          
          if (Array.isArray(value)) {
            value = value.join(', ');
          } else if (typeof value === 'boolean') {
            value = value ? 'Sim' : 'Não';
          } else if (value === null || value === undefined || value === '') {
            value = 'N/A';
          }
          
          row[label] = value;
        }
      });
      
      return row;
    });

    // Criar CSV manualmente com BOM para UTF-8
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(';'),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          const stringValue = String(value);
          
          // Escapar aspas duplas duplicando-as
          const escapedValue = stringValue.replace(/"/g, '""');
          
          // Envolver em aspas se contém: ponto e vírgula, quebras de linha, vírgulas, aspas ou espaços no início/fim
          const needsQuotes = escapedValue.includes(';') || 
                             escapedValue.includes('\n') || 
                             escapedValue.includes('\r') || 
                             escapedValue.includes(',') || 
                             escapedValue.includes('"') ||
                             escapedValue !== escapedValue.trim();
          
          return needsQuotes ? `"${escapedValue}"` : escapedValue;
        }).join(';')
      )
    ].join('\n');

    // Adicionar BOM UTF-8 para garantir codificação correta
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Criar e baixar arquivo
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `respostas_osc_completas_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Aplicar filtros quando activeFilters mudar
  useEffect(() => {
    applyFilters();
  }, [activeFilters, allResponses]);

  // Aplicar paginação quando filteredResponses, currentPage ou pageSize mudarem
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filteredResponses.slice(startIndex, endIndex);
    setPaginatedResponses(paginated);
  }, [filteredResponses, currentPage, pageSize]);

  // Resetar página atual quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters]);

  const fetchOSCsWithoutResponses = async () => {
    setLoadingOSCsWithoutResponses(true);
    try {
      // Buscar todas as OSCs cadastradas
      const oscsRef = ref(database, 'oscs');
      const oscsSnapshot = await get(oscsRef);
      
      // Buscar todas as respostas completas
      const responsesRef = ref(database, 'responses');
      const responsesSnapshot = await get(responsesRef);
      
      if (oscsSnapshot.exists()) {
        const oscsData = oscsSnapshot.val();
        const oscsArray = Object.keys(oscsData).map(key => ({
          id: key,
          ...oscsData[key]
        }));
        
        // Identificar OSCs com respostas completas
        const oscsWithCompleteResponses = new Set();
        
        if (responsesSnapshot.exists()) {
          const responsesData = responsesSnapshot.val();
          Object.values(responsesData).forEach((response: any) => {
            if (response.oscId) {
              oscsWithCompleteResponses.add(response.oscId);
            }
          });
        }
        
        // Filtrar OSCs sem respostas completas
        const oscsWithoutCompleteResponses = oscsArray.filter(osc => 
          !oscsWithCompleteResponses.has(osc.id)
        );
        
        setOscsWithoutResponses(oscsWithoutCompleteResponses);
      } else {
        setOscsWithoutResponses([]);
      }
    } catch (error) {
      
      setOscsWithoutResponses([]);
    } finally {
      setLoadingOSCsWithoutResponses(false);
    }
  };

  // Lista de perguntas do formulário para identificar a pergunta atual
  const questions = [
    { id: 'atividades', title: 'Nos conte sobre as atividades da OSC' },
    { id: 'numeroFuncionarios', title: 'Qual é o número de funcionários da organização?' },
    { id: 'numeroVoluntarios', title: 'Qual é o número de voluntários da organização?' },
    { id: 'pessoasAtendidasMes', title: 'Quantas pessoas a organização atende por mês?' },
    { id: 'suspendeAtividades', title: 'A organização costuma suspender suas atividades em algum período do ano?' },
    { id: 'mesesSuspensao', title: 'Indique os meses em que a organização costuma suspender suas atividades' },
    { id: 'documentos', title: 'Dos documentos listados, assinale quais dessas alternativas a entidade possui' },
    { id: 'publicoAtendido', title: 'Selecione o público que a instituição atende' },
    { id: 'publicoAtendidoOutros', title: 'Especifique outros públicos atendidos' },
    { id: 'criancas', title: 'Quantas Crianças (0 a 12 anos) a organização atende?' },
    { id: 'adolescentes', title: 'Quantos Adolescentes (13 a 17 anos) a organização atende?' },
    { id: 'jovens', title: 'Quantos Jovens (18 a 29 anos) a organização atende?' },
    { id: 'adultos', title: 'Quantos Adultos (30 a 59 anos) a organização atende?' },
    { id: 'idosos', title: 'Quantas Pessoas Idosas (acima dos 60 anos) a organização atende?' },
    { id: 'possuiFichaCadastral', title: 'Possui ficha cadastral com dados socioeconômicos dos atendidos?' },
    { id: 'beneficiariosMensais', title: 'Quantas pessoas se beneficiam mensalmente dos alimentos?' },
    { id: 'infraestrutura', title: 'Assinale quais destas alternativas a entidade possui' },
    { id: 'serveRefeicoes', title: 'Servem refeições no local?' },
    { id: 'serveCafeManha', title: 'Servem Café da manhã?' },
    { id: 'pessoasCafeManha', title: 'Pra quantas pessoas o café da manhã é servido ao longo do mês?' },
    { id: 'diasCafeManha', title: 'Quantos dias por semana é servido o café da manhã?' },
    { id: 'serveLancheManha', title: 'Servem Lanche da manhã?' },
    { id: 'pessoasLancheManha', title: 'Pra quantas pessoas o lanche da manhã é servido?' },
    { id: 'diasLancheManha', title: 'Quantos dias por semana é servido o lanche da manhã?' },
    { id: 'serveAlmoco', title: 'Servem Almoço?' },
    { id: 'pessoasAlmoco', title: 'Pra quantas pessoas o almoço é servido ao longo do mês?' },
    { id: 'diasAlmoco', title: 'Quantos dias por semana é servido o almoço?' },
    { id: 'serveLancheTarde', title: 'Servem Lanche da Tarde?' },
    { id: 'pessoasLancheTarde', title: 'Pra quantas pessoas o lanche da tarde é servido?' },
    { id: 'diasLancheTarde', title: 'Quantos dias por semana é servido o lanche da tarde?' },
    { id: 'serveJantar', title: 'Servem Jantar?' },
    { id: 'pessoasJantar', title: 'Pra quantas pessoas o jantar é servido ao longo do mês?' },
    { id: 'diasJantar', title: 'Quantos dias por semana é servido o jantar?' },
    { id: 'distribuiAlimentos', title: 'Sua organização distribui alimentos para pessoas em situação de vulnerabilidade?' },
    { id: 'distribuiCestasBasicas', title: 'Sua organização realiza distribuição de Cestas Básicas?' },
    { id: 'quantidadeCestasBasicas', title: 'Qual a quantidade total distribuída em média por mês de Cestas Básicas?' },
    { id: 'distribuiMarmitas', title: 'Sua organização realiza distribuição de Marmitas/ Quentinhas?' },
    { id: 'quantidadeMarmitas', title: 'Qual a quantidade total distribuída em média por mês de Marmitas?' },
    { id: 'distribuiKitFrutas', title: 'Sua organização realiza distribuição de Kit/ Sacolinha de frutas?' },
    { id: 'quantidadeKitFrutas', title: 'Qual a quantidade total distribuída em média por mês de Kit de frutas?' },
    { id: 'atividadesAlimentacao', title: 'Realizam alguma atividade voltada para a alimentação?' },
    { id: 'tiposAtividadesAlimentacao', title: 'Quais atividades voltadas para a alimentação são realizadas?' },
    { id: 'outrasAtividadesAlimentacao', title: 'Especifique outras atividades' },
    { id: 'descricaoAtividadesAlimentacao', title: 'Conte-nos um pouco sobre as atividades realizadas' },
    { id: 'transfereDoacoes', title: 'Existe a intenção de transferir doações para outra unidade?' },
    { id: 'unidadesBeneficiadas', title: 'Liste as unidades ou filiais beneficiadas' },
    { id: 'restricoesColeta', title: 'Possui algum tipo de restrição para coletar doações?' },
    { id: 'descricaoRestricoes', title: 'Descreva as restrições para coletar doações' },
    { id: 'disponibilidadeColeta', title: 'Qual seria a disponibilidade da instituição para coletar as doações?' },
    { id: 'recebeDoacoesOutrosParceiros', title: 'Recebem doações de alimentos de outros parceiros?' },
    { id: 'tiposAlimentosRecebidos', title: 'Selecione os tipos de alimento que a instituição recebe' },
    { id: 'outrosTiposAlimentos', title: 'Especifique outros tipos de alimentos' },
    { id: 'recebeBancoAlimentos', title: 'Recebe doação de Banco de Alimentos?' },
    { id: 'quaisBancosAlimentos', title: 'De quais Banco de Alimentos recebem doação?' },
    { id: 'recebeSescMesaBrasil', title: 'Recebe doação do Sesc Mesa Brasil?' },
    { id: 'unidadesSescMesaBrasil', title: 'De quais unidades do Sesc Mesa Brasil recebem doações?' },
    { id: 'recebeDoacoesGoverno', title: 'Recebe doação de Prefeitura/ Governo?' },
    { id: 'recebeDoacoesPessoasFisicas', title: 'Recebe doação de pessoas físicas?' },
    { id: 'frequenciaDoacoesOutrosParceiros', title: 'Selecione a frequência com que a organização recebe doação' },
    { id: 'possuiTransporte', title: 'Possui transporte para retirar as doações?' },
    { id: 'tipoTransporte', title: 'Especifique se o transporte é próprio ou de terceiros' },
    { id: 'veiculoColeta', title: 'Que veículo seria utilizado para coleta?' },
    { id: 'avaliacaoFormulario', title: 'O que você achou da experiência deste formulário?' },
    { id: 'comentarioExperiencia', title: 'Deixe um comentário sobre sua experiência' },
    { id: 'probabilidadeRecomendacao', title: 'Qual a probabilidade de você nos recomendar?' }
  ];

  const fetchOSCsWithPartialResponses = async () => {
    setLoadingOSCsWithPartialResponses(true);
    try {
      
      
      // Buscar todas as OSCs cadastradas
      const oscsRef = ref(database, 'oscs');
      const oscsSnapshot = await get(oscsRef);
      
      // Buscar todas as respostas parciais
      const partialResponsesRef = ref(database, 'partialResponses');
      const partialResponsesSnapshot = await get(partialResponsesRef);
      
      // Buscar todas as respostas completas
      const responsesRef = ref(database, 'responses');
      const responsesSnapshot = await get(responsesRef);
      
      
      
      if (oscsSnapshot.exists() && partialResponsesSnapshot.exists()) {
        const oscsData = oscsSnapshot.val();
        const partialResponsesData = partialResponsesSnapshot.val();
        const responsesData = responsesSnapshot.exists() ? responsesSnapshot.val() : {};
        
        
        
        
        // Identificar OSCs com respostas completas
        const oscsWithCompleteResponses = new Set();
        Object.values(responsesData).forEach((response: any) => {
          if (response.oscId) {
            oscsWithCompleteResponses.add(response.oscId);
          }
        });
        
        
        
        // Filtrar OSCs com respostas parciais mas sem respostas completas
        const oscsWithPartialOnly = Object.keys(partialResponsesData)
          .filter(oscId => {
            const hasComplete = oscsWithCompleteResponses.has(oscId);
            
            return !hasComplete;
          })
          .map(oscId => {
            const oscData = oscsData[oscId];
            const partialData = partialResponsesData[oscId];
            
            
            
            // Determinar a pergunta atual baseada no currentQuestionIndex
            const currentQuestionIndex = partialData.currentQuestionIndex || 0;
            const currentQuestion = questions[currentQuestionIndex] || { title: 'Pergunta não identificada' };
            
            return {
              id: oscId,
              ...oscData,
              currentQuestionTitle: currentQuestion.title,
              currentQuestionIndex: currentQuestionIndex + 1,
              lastSaved: partialData.lastSaved
            };
          })
          .filter(osc => {
            const hasRazaoSocial = !!osc.razaoSocial;
            
            return hasRazaoSocial;
          });
        
        
        
        setOscsWithPartialResponses(oscsWithPartialOnly);
      } else {
        
        setOscsWithPartialResponses([]);
      }
    } catch (error) {
      
      setOscsWithPartialResponses([]);
    } finally {
      setLoadingOSCsWithPartialResponses(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Buscar todas as OSCs
      const oscsRef = ref(database, 'oscs');
      const oscsSnapshot = await get(oscsRef);
      const oscsData = oscsSnapshot.exists() ? oscsSnapshot.val() : {};
      
      // Buscar todas as respostas
      const responsesRef = ref(database, 'responses');
      const responsesSnapshot = await get(responsesRef);
      const responsesData = responsesSnapshot.exists() ? responsesSnapshot.val() : {};
      
      // Buscar respostas parciais
      const partialResponsesRef = ref(database, 'partialResponses');
      const partialResponsesSnapshot = await get(partialResponsesRef);
      const partialResponsesData = partialResponsesSnapshot.exists() ? partialResponsesSnapshot.val() : {};
      
      const totalOSCs = Object.keys(oscsData).length;
      
      // Contar total de respostas finalizadas
      const totalResponses = Object.keys(responsesData).length;
      
      // OSCs com pelo menos uma resposta completa
      const oscsWithCompleteResponses = new Set();
      Object.values(responsesData).forEach((response: any) => {
        if (response.oscId) {
          oscsWithCompleteResponses.add(response.oscId);
        }
      });
      
      // OSCs com respostas parciais
      const oscsWithPartialResponses = new Set();
      Object.keys(partialResponsesData).forEach((oscId) => {
        oscsWithPartialResponses.add(oscId);
      });
      
      // OSCs sem respostas completas - usar a mesma lógica do modal
      const oscsArray = Object.keys(oscsData).map(key => ({
        id: key,
        ...oscsData[key]
      }));
      const oscsWithoutCompleteResponsesArray = oscsArray.filter(osc => 
        !oscsWithCompleteResponses.has(osc.id)
      );
      const oscsWithoutCompleteResponses = oscsWithoutCompleteResponsesArray.length;
      
      // OSCs sem respostas completas mas com respostas em andamento
      const oscsWithPartialButNoComplete = new Set();
      oscsWithPartialResponses.forEach((oscId) => {
        if (!oscsWithCompleteResponses.has(oscId)) {
          oscsWithPartialButNoComplete.add(oscId);
        }
      });
      
      setStats({
        totalOSCs,
        oscsWithResponses: oscsWithCompleteResponses.size,
        oscsWithoutCompleteResponses,
        oscsWithPartialResponses: oscsWithPartialButNoComplete.size,
        totalResponses
      });
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDashboardStats();
    fetchAdminName();
    loadResponsesData();
  }, [currentUser, fetchAdminName]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin');
    } catch (error) {
      
    }
  };

  const handleOpenPasswordModal = () => {
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('As senhas não coincidem!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres!');
      return;
    }

    try {
      setPasswordLoading(true);
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      alert('Senha alterada com sucesso!');
      handleClosePasswordModal();
    } catch (error: any) {
      alert(error.message || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };



  return (
    <AdminContainer>
      <AdminHeader>
        <div>
          <h2>🛡️ Painel Administrativo</h2>
          <StatusBadge>ADMIN</StatusBadge>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <UserInfoButton onClick={handleOpenPasswordModal} style={{ background: '#6c757d' }}>
            <UserInfo>
              <UserName>Alterar Senha</UserName>
            </UserInfo>
            <LogoutIcon>🔒</LogoutIcon>
          </UserInfoButton>
          <UserInfoButton onClick={handleLogout}>
            <UserInfo>
              <UserName>{adminName || 'Administrador'}</UserName>
              <UserEmail>{currentUser?.email}</UserEmail>
            </UserInfo>
            <LogoutIcon>🚪</LogoutIcon>
          </UserInfoButton>
        </div>
      </AdminHeader>

      {loading ? (
        <LoadingCard>
          <h3>📊 Carregando Estatísticas...</h3>
          <p>Aguarde enquanto coletamos os dados das OSCs</p>
        </LoadingCard>
      ) : (
        <>
          <AdminCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', margin: 0 }}>
                📊 Dashboard - Estatísticas das OSCs
              </h2>
              <RefreshButton onClick={fetchDashboardStats}>
                🔄 Atualizar
              </RefreshButton>
            </div>
            
            <StatsGrid>
              <StatCard className="primary">
                <StatNumber>{stats.totalOSCs}</StatNumber>
                <StatLabel>Total de OSCs</StatLabel>
                <StatDescription>Organizações cadastradas no sistema</StatDescription>
              </StatCard>
              
              <StatCard className="success">
                <StatNumber>{stats.oscsWithResponses}</StatNumber>
                <StatLabel>OSCs com Respostas</StatLabel>
                <StatDescription>OSCs que têm pelo menos uma resposta completa</StatDescription>
              </StatCard>
              
              <StatCard 
                className="warning" 
                onClick={() => {
                  setShowOSCsWithoutResponsesModal(true);
                  fetchOSCsWithoutResponses();
                }}
                style={{ cursor: 'pointer' }}
              >
                <StatNumber>{stats.oscsWithoutCompleteResponses}</StatNumber>
                <StatLabel>OSCs sem Respostas</StatLabel>
                <StatDescription>OSCs que ainda não completaram nenhuma resposta</StatDescription>
              </StatCard>
              
              <StatCard 
                className="info"
                onClick={() => {
                  setShowPartialResponsesModal(true);
                  fetchOSCsWithPartialResponses();
                }}
                style={{ cursor: 'pointer' }}
              >
                <StatNumber>{stats.oscsWithPartialResponses}</StatNumber>
                <StatLabel>Respostas em Andamento</StatLabel>
                <StatDescription>OSCs com respostas parciais (não finalizadas)</StatDescription>
              </StatCard>
              
              <StatCard className="secondary">
                <StatNumber>{stats.totalResponses}</StatNumber>
                <StatLabel>Respostas Finalizadas</StatLabel>
                <StatDescription>Total de formulários completados e enviados</StatDescription>
              </StatCard>
            </StatsGrid>
          </AdminCard>
          
          <AdminCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', margin: 0 }}>
                📊 Dashboard - Dados
              </h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                <RefreshButton onClick={loadResponsesData}>
                  🔄 Atualizar Dados
                </RefreshButton>
                <RefreshButton onClick={exportToCSV} style={{ background: '#28a745' }}>
                  📄 Exportar CSV
                </RefreshButton>
              </div>
            </div>
            
            <FiltersContainer>
              <FilterRow>
                <FilterSelect 
                  value={selectedFilterField} 
                  onChange={(e) => {
                    setSelectedFilterField(e.target.value);
                    setSelectedOperator('='); // Reset operator when field changes
                  }}
                >
                  <option value="">Selecione um campo para filtrar</option>
                  {availableFields.map(field => (
                    <option key={field.key} value={field.key}>{field.label}</option>
                  ))}
                </FilterSelect>
                
                {selectedFilterField && (
                  <FilterSelect 
                    value={selectedOperator} 
                    onChange={(e) => setSelectedOperator(e.target.value)}
                  >
                    {getAvailableOperators(getSelectedFieldType()).map(operator => (
                      <option key={operator.value} value={operator.value}>{operator.label}</option>
                    ))}
                  </FilterSelect>
                )}
                
                {selectedFilterField === 'publicoAtendido' ? (
                  <FilterSelect 
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  >
                    <option value="">Selecione um público</option>
                    {publicoAtendidoOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </FilterSelect>
                ) : selectedFilterField === 'mesesSuspensao' ? (
                  <FilterSelect 
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  >
                    <option value="">Selecione um mês</option>
                    {mesesSuspensaoOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </FilterSelect>
                ) : selectedFilterField === 'documentos' ? (
                  <FilterSelect 
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  >
                    <option value="">Selecione um documento</option>
                    {documentosOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </FilterSelect>
                ) : selectedFilterField === 'infraestrutura' ? (
                  <FilterSelect 
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  >
                    <option value="">Selecione um tipo</option>
                    {infraestruturaOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </FilterSelect>
                ) : (selectedFilterField === 'suspendeAtividades' || selectedFilterField === 'possuiFichaCadastral') ? (
                  <FilterSelect 
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  >
                    <option value="">Selecione uma opção</option>
                    {booleanOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </FilterSelect>
                ) : (
                  <FilterInput 
                    type={getSelectedFieldType() === 'date' ? 'date' : getSelectedFieldType() === 'number' ? 'number' : 'text'}
                    placeholder={
                      selectedFilterField === 'cnpj' ? 'XX.XXX.XXX/XXXX-XX' : 
                      getSelectedFieldType() === 'date' ? 'Selecione uma data' :
                      getSelectedFieldType() === 'number' ? 'Digite um número' :
                      'Digite o valor do filtro'
                    } 
                    value={filterValue}
                    onChange={(e) => handleFilterValueChange(e.target.value)}
                    maxLength={selectedFilterField === 'cnpj' ? 18 : undefined}
                  />
                )}
                <Button onClick={addFilter} disabled={!selectedFilterField || !filterValue}>
                  Adicionar Filtro
                </Button>
              </FilterRow>
              
              {activeFilters.length > 0 && (
                <ActiveFiltersContainer>
                  <h4>Filtros Ativos:</h4>
                  <FilterTags>
                    {activeFilters.map((filter, index) => (
                      <FilterTag key={index} onClick={() => editFilter(index)}>
                        <strong>{getFieldLabel(filter.field)}</strong> {getOperatorLabel(filter.operator)} <strong>{filter.value}</strong>
                        <RemoveFilterButton onClick={(e) => {
                          e.stopPropagation();
                          removeFilter(index);
                        }}>×</RemoveFilterButton>
                      </FilterTag>
                    ))}
                    <ClearAllFiltersButton onClick={clearAllFilters}>
                      Limpar Todos
                    </ClearAllFiltersButton>
                  </FilterTags>
                </ActiveFiltersContainer>
              )}
            </FiltersContainer>
            
            <DataTableContainer>
              {loadingData ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <h3>📊 Carregando dados...</h3>
                  <p>Aguarde enquanto coletamos as respostas</p>
                </div>
              ) : (
                <>
                  <TableInfo>
                    Exibindo {filteredResponses.length} de {allResponses.length} respostas
                    <span style={{ marginLeft: '16px', fontSize: '12px', color: '#6c757d' }}>💡 Clique em uma linha para ver os detalhes</span>
                  </TableInfo>
                  <TableScrollContainer>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>Data da Resposta</th>
                          <th>Nome da OSC</th>
                          <th>CNPJ</th>
                          <th>Responsável</th>
                          <th>Cargo do Responsável</th>
                          <th>Telefone</th>
                          <th>Email</th>
                          <th>Email do Responsável</th>
                          <th>Cidade</th>
                          <th>UF</th>
                          <th>Funcionários</th>
                          <th>Voluntários</th>
                          <th>Pessoas Atendidas/Mês</th>
                        </tr>
                      </thead>
                      <tbody>
                         {paginatedResponses.map((response, index) => (
                           <ClickableRow key={response.id || index} onClick={() => viewResponseDetails(response)}>
                             <td>{formatDate(response.createdAt)}</td>
                             <td>{response.nomeOrganizacao || 'N/A'}</td>
                             <td>{response.cnpj || 'N/A'}</td>
                             <td>{response.nomeResponsavel || 'N/A'}</td>
                             <td>{response.cargoResponsavel || 'N/A'}</td>
                             <td>{response.telefone || 'N/A'}</td>
                             <td>{response.email || 'N/A'}</td>
                             <td>{response.emailResponsavel || 'N/A'}</td>
                             <td>{response.cidade || 'N/A'}</td>
                             <td>{response.uf || 'N/A'}</td>
                             <td>{response.numeroFuncionarios || 'N/A'}</td>
                             <td>{response.numeroVoluntarios || 'N/A'}</td>
                             <td>{response.pessoasAtendidasMes || 'N/A'}</td>
                           </ClickableRow>
                         ))}
                       </tbody>
                    </DataTable>
                  </TableScrollContainer>
                  {filteredResponses.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                      <p>Nenhuma resposta encontrada com os filtros aplicados.</p>
                    </div>
                  )}
                </>
              )}
              
              {/* Controles de Paginação */}
              {filteredResponses.length > 0 && (
                <PaginationContainer>
                  <PaginationInfo>
                    Mostrando {startItem} a {endItem} de {filteredResponses.length} registros
                  </PaginationInfo>
                  
                  <PaginationControls>
                    <PaginationButton 
                      onClick={handlePreviousPage} 
                      disabled={currentPage === 1}
                    >
                      ← Anterior
                    </PaginationButton>
                    
                    <span style={{ fontSize: '14px', color: '#6c757d' }}>
                      Página {currentPage} de {totalPages}
                    </span>
                    
                    <PaginationButton 
                      onClick={handleNextPage} 
                      disabled={currentPage === totalPages}
                    >
                      Próximo →
                    </PaginationButton>
                    
                    <PageSizeSelect 
                      value={pageSize} 
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    >
                      <option value={10}>10 por página</option>
                      <option value={25}>25 por página</option>
                      <option value={50}>50 por página</option>
                      <option value={100}>100 por página</option>
                    </PageSizeSelect>
                  </PaginationControls>
                </PaginationContainer>
              )}
            </DataTableContainer>
          </AdminCard>

        </>      )}      
      {/* Modal para OSCs sem Respostas */}
      {showOSCsWithoutResponsesModal && (
        <Modal onClick={() => {
          setShowOSCsWithoutResponsesModal(false);
          setOscsWithoutResponsesSearchTerm('');
        }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <h2>📋 OSCs sem Respostas Completas</h2>
                <div className="subtitle">Lista de OSCs que ainda não completaram nenhuma resposta</div>
              </div>
              <CloseButton onClick={() => {
                setShowOSCsWithoutResponsesModal(false);
                setOscsWithoutResponsesSearchTerm('');
              }}>×</CloseButton>
            </ModalHeader>
            
            {loadingOSCsWithoutResponses ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <h3>📊 Carregando OSCs...</h3>
                <p>Aguarde enquanto coletamos as informações</p>
              </div>
            ) : (
              <>
                {oscsWithoutResponses.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                    <h3>🎉 Parabéns!</h3>
                    <p>Todas as OSCs cadastradas já possuem pelo menos uma resposta completa.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                      <div style={{ color: '#6c757d' }}>
                        Total de OSCs sem respostas completas: <strong>{oscsWithoutResponses.filter(osc => osc.razaoSocial?.toLowerCase().includes(oscsWithoutResponsesSearchTerm.toLowerCase())).length}</strong>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label htmlFor="oscsWithoutResponsesSearch" style={{ fontSize: '14px', color: '#6c757d', whiteSpace: 'nowrap' }}>🔍 Buscar:</label>
                        <input
                          id="oscsWithoutResponsesSearch"
                          type="text"
                          placeholder="Digite a razão social..."
                          value={oscsWithoutResponsesSearchTerm}
                          onChange={(e) => setOscsWithoutResponsesSearchTerm(e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            minWidth: '200px',
                            outline: 'none',
                            transition: 'border-color 0.2s ease',
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#007bff'}
                          onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        />
                      </div>
                    </div>
                    
                    <DataTableContainer>
                      <DataTable>
                        <thead>
                          <tr>
                            <th>ID da OSC</th>
                            <th>Razão Social</th>
                            <th>CNPJ</th>
                            <th>E-mail</th>
                            <th>Responsável</th>
                            <th>Cargo</th>
                            <th>WhatsApp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {oscsWithoutResponses
                            .filter(osc => osc.razaoSocial?.toLowerCase().includes(oscsWithoutResponsesSearchTerm.toLowerCase()))
                            .map((osc, index) => (
                            <DataTableRow key={osc.id || index}>
                              <td>{osc.id || 'Não informado'}</td>
                              <td>{osc.razaoSocial || 'Não informado'}</td>
                              <td>{osc.cnpj || 'Não informado'}</td>
                              <td>{osc.email || 'Não informado'}</td>
                              <td>{osc.nomeResponsavel || 'Não informado'}</td>
                              <td>{osc.cargoResponsavel || 'Não informado'}</td>
                              <td>{osc.whatsappResponsavel || 'Não informado'}</td>
                            </DataTableRow>
                          ))}
                        </tbody>
                      </DataTable>
                    </DataTableContainer>
                  </>
                )}
              </>
            )}
          </ModalContent>
        </Modal>
      )}
      
      {/* Modal para Respostas em Andamento */}
      {showPartialResponsesModal && (
        <Modal onClick={() => {
          setShowPartialResponsesModal(false);
          setPartialResponsesSearchTerm('');
        }}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', width: '95%' }}>
            <ModalHeader>
              <div>
                <h2>⏳ OSCs com Respostas em Andamento</h2>
                <div className="subtitle">Lista de OSCs que iniciaram mas não finalizaram o formulário</div>
              </div>
              <CloseButton onClick={() => {
                setShowPartialResponsesModal(false);
                setPartialResponsesSearchTerm('');
              }}>×</CloseButton>
            </ModalHeader>
            
            {loadingOSCsWithPartialResponses ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <h3>📊 Carregando OSCs...</h3>
                <p>Aguarde enquanto coletamos as informações</p>
              </div>
            ) : (
              <>
                {oscsWithPartialResponses.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                    <h3>📝 Nenhuma resposta em andamento</h3>
                    <p>Não há OSCs com formulários iniciados mas não finalizados.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                      <div style={{ color: '#6c757d' }}>
                        Total de OSCs com respostas em andamento: <strong>{oscsWithPartialResponses.filter(osc => 
                          osc.razaoSocial?.toLowerCase().includes(partialResponsesSearchTerm.toLowerCase())
                        ).length}</strong>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>🔍 Buscar:</label>
                        <input
                          type="text"
                          placeholder="Digite a razão social..."
                          value={partialResponsesSearchTerm}
                          onChange={(e) => setPartialResponsesSearchTerm(e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #ced4da',
                            borderRadius: '6px',
                            fontSize: '14px',
                            minWidth: '250px',
                            outline: 'none',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#0d6efd'}
                          onBlur={(e) => e.target.style.borderColor = '#ced4da'}
                        />
                      </div>
                    </div>
                    
                    <DataTableContainer>
                      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                        <DataTable style={{ minWidth: '1000px' }}>
                          <thead>
                            <tr>
                              <th style={{ minWidth: '100px' }}>ID da OSC</th>
                              <th style={{ minWidth: '200px' }}>Razão Social</th>
                              <th style={{ minWidth: '300px' }}>Pergunta Atual</th>
                              <th style={{ minWidth: '200px' }}>Progresso</th>
                              <th style={{ minWidth: '180px' }}>Última Atualização</th>
                            </tr>
                          </thead>
                          <tbody>
                            {oscsWithPartialResponses
                              .filter(osc => 
                                osc.razaoSocial?.toLowerCase().includes(partialResponsesSearchTerm.toLowerCase())
                              )
                              .map((osc, index) => (
                              <DataTableRow key={osc.id || index}>
                                <td style={{ minWidth: '100px' }}>{osc.id || 'Não informado'}</td>
                                <td style={{ minWidth: '200px' }}>{osc.razaoSocial || 'Não informado'}</td>
                                <td style={{ minWidth: '300px', wordWrap: 'break-word' }}>
                                  {osc.currentQuestionTitle || 'Pergunta não identificada'}
                                </td>
                                <td style={{ minWidth: '200px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>Pergunta {osc.currentQuestionIndex || 1} de 60</span>
                                    <div style={{
                                      width: '60px',
                                      height: '6px',
                                      backgroundColor: '#e9ecef',
                                      borderRadius: '3px',
                                      overflow: 'hidden'
                                    }}>
                                      <div style={{
                                        width: `${((osc.currentQuestionIndex || 1) / 60) * 100}%`,
                                        height: '100%',
                                        backgroundColor: '#007bff',
                                        borderRadius: '3px'
                                      }}></div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ minWidth: '180px' }}>
                                  {osc.lastSaved ? 
                                    new Date(osc.lastSaved).toLocaleString('pt-BR') : 
                                    'Não informado'
                                  }
                                </td>
                              </DataTableRow>
                            ))}
                          </tbody>
                        </DataTable>
                      </div>
                    </DataTableContainer>
                  </>
                )}
              </>
            )}
          </ModalContent>
        </Modal>
      )}

      {/* Modal de Detalhes da Resposta */}
      {selectedResponse && (
        <Modal onClick={() => setSelectedResponse(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <h2>📋 Detalhes da Resposta</h2>
                <div className="subtitle">Informações completas do formulário</div>
              </div>
              <CloseButton onClick={() => setSelectedResponse(null)}>×</CloseButton>
            </ModalHeader>
            
            <DetailCard>
              <h3>📊 Informações Básicas</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="label">Data da Resposta:</div>
                  <div className="value">{formatDate(selectedResponse.createdAt)}</div>
                </div>
                <div className="detail-item">
                  <div className="label">ID da Resposta:</div>
                  <div className="value">{selectedResponse.id}</div>
                </div>
                {selectedResponse.oscId && (
                  <div className="detail-item">
                    <div className="label">ID da OSC:</div>
                    <div className="value">{selectedResponse.oscId}</div>
                  </div>
                )}
              </div>
            </DetailCard>
            
            {questionOrder.map(key => {
              const value = selectedResponse[key];
              if (value === null || value === undefined || value === '') return null;
              
              return (
                <DetailCard key={key}>
                  <h3>{getDetailFieldLabel(key)}</h3>
                  <div className="detail-item">
                    <div className="value">{formatValue(value)}</div>
                  </div>
                </DetailCard>
              );
            })}
          </ModalContent>
        </Modal>
      )}

      {/* Modal de Alteração de Senha */}
      {showPasswordModal && (
        <Modal onClick={handleClosePasswordModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>🔒 Alterar Senha</h2>
            
            <FormGroup>
              <label>Senha Atual:</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                placeholder="Digite sua senha atual"
              />
            </FormGroup>

            <FormGroup>
              <label>Nova Senha:</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                placeholder="Digite a nova senha (mín. 6 caracteres)"
              />
            </FormGroup>

            <FormGroup>
              <label>Confirmar Nova Senha:</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                placeholder="Confirme a nova senha"
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton 
                onClick={handleClosePasswordModal}
                disabled={passwordLoading}
              >
                Cancelar
              </CancelButton>
              <SaveButton 
                onClick={handleChangePassword}
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Alterando...' : 'Alterar Senha'}
              </SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </AdminContainer>
  );
};

export default AdminDashboard;